// ./src/hooks/useVehicleFinancing.ts
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  VehicleFinancing,
  VehicleFinancingService,
  VehicleFinancingInput,
  VehicleFinancingFilter,
  VEHICLE_FINANCING_STATUS,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { Pagination, SortItem } from "@/utils/api/commonApiTypes";

// ============================================
// Types
// ============================================

export interface UseVehicleFinancingOptions {
  /** Auto-load data on mount (default: false) */
  autoLoad?: boolean;
  /** Initial filter to apply */
  initialFilter?: VehicleFinancingFilter;
  /** Initial pagination settings */
  initialPagination?: Pagination;
  /** Initial sort settings */
  initialSortBy?: SortItem[];
  /** Car ID to filter by */
  carId?: string;
}

export interface UseVehicleFinancingReturn {
  // Data state
  financingRecords: VehicleFinancing[];
  selectedFinancing: VehicleFinancing | null;
  isLoading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isRemoving: boolean;

  // Filter and pagination
  filter: VehicleFinancingFilter;
  pagination: Pagination;
  sortBy: SortItem[];
  setFilter: (filter: VehicleFinancingFilter) => void;
  setPagination: (pagination: Pagination) => void;
  setSortBy: (sortBy: SortItem[]) => void;

  // Core operations - return OpResult for immediate access to result
  list: (
    filter?: VehicleFinancingFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ) => Promise<OpResult>;
  get: (id: string) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: VehicleFinancingInput) => Promise<OpResult>;
  update: (id: string, params: VehicleFinancingInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Convenience methods
  refresh: () => Promise<OpResult>;
  select: (financing: VehicleFinancing | null) => void;
  clearSelection: () => void;

  // Helper operations
  reset: () => void;

  // Derived data helpers
  /** Get the active financing record for a specific car (if any) */
  getActiveForCar: (carId: string) => VehicleFinancing | undefined;

  // Error handling - standardized ApiError with built-in checkers
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_PAGINATION: Pagination = {
  page: 1,
  pageSize: 50,
};

const DEFAULT_SORT_BY: SortItem[] = [
  { name: "startDate", order: "DESC" },
];

// ============================================
// Hook Implementation
// ============================================

export const useVehicleFinancing = (
  options: UseVehicleFinancingOptions = {}
): UseVehicleFinancingReturn => {
  const {
    autoLoad = false,
    initialFilter = {},
    initialPagination = DEFAULT_PAGINATION,
    initialSortBy = DEFAULT_SORT_BY,
    carId,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const userIdRef = useRef<string | null>(userId);

  // Update userIdRef when userId changes
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Data state
  const [financingRecords, setFinancingRecords] = useState<VehicleFinancing[]>([]);
  const [selectedFinancing, setSelectedFinancing] = useState<VehicleFinancing | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Filter and pagination state
  const [filter, setFilter] = useState<VehicleFinancingFilter>(() => ({
    ...initialFilter,
    ...(carId ? { carId: [carId] } : {}),
  }));
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSortBy);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setFinancingRecords([]);
    setSelectedFinancing(null);
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
    setIsCreating(false);
    setIsUpdating(false);
    setIsRemoving(false);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // List vehicle financing records
  const list = useCallback(
    async (
      filterOverride?: VehicleFinancingFilter,
      paginationOverride?: Pagination,
      sortByOverride?: SortItem[]
    ): Promise<OpResult> => {
      setIsLoading(true);
      setLastError(null);

      try {
        const effectiveFilter = filterOverride ?? filter;
        const effectivePagination = paginationOverride ?? pagination;
        const effectiveSortBy = sortByOverride ?? sortBy;

        const result = await VehicleFinancingService.list(
          effectiveFilter,
          effectivePagination,
          effectiveSortBy
        );

        if (result.code === OP_RESULT_CODES.OK) {
          const data = (result.data as VehicleFinancing[]) || [];
          setFinancingRecords(data);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load financing records"));
        setIsLoaded(true);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load financing records"));
        setIsLoaded(true);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [filter, pagination, sortBy]
  );

  // Get single vehicle financing record
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setIsLoading(true);
    setLastError(null);

    try {
      const result = await VehicleFinancingService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = Array.isArray(result.data)
          ? (result.data as VehicleFinancing[])[0]
          : (result.data as VehicleFinancing);
        setSelectedFinancing(data);
        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to load financing record"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to load financing record"));
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get multiple vehicle financing records
  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    setIsLoading(true);
    setLastError(null);

    try {
      const result = await VehicleFinancingService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK) {
        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to load financing records"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to load financing records"));
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create vehicle financing record
  const create = useCallback(
    async (params: VehicleFinancingInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await VehicleFinancingService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newFinancing = Array.isArray(result.data)
            ? (result.data as VehicleFinancing[])[0]
            : (result.data as VehicleFinancing);

          // Add to list
          setFinancingRecords((prev) => [newFinancing, ...prev]);
          setSelectedFinancing(newFinancing);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create financing record"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create financing record"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update vehicle financing record
  const update = useCallback(
    async (id: string, params: VehicleFinancingInput): Promise<OpResult> => {
      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await VehicleFinancingService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedFinancing = Array.isArray(result.data)
            ? (result.data as VehicleFinancing[])[0]
            : (result.data as VehicleFinancing);

          // Update in list
          setFinancingRecords((prev) =>
            prev.map((f) => (f.id === id ? updatedFinancing : f))
          );

          // Update selected if it's the same
          setSelectedFinancing((prev) =>
            prev?.id === id ? updatedFinancing : prev
          );

          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update financing record"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update financing record"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Remove vehicle financing record
  const remove = useCallback(async (id: string): Promise<OpResult> => {
    setIsRemoving(true);
    setLastError(null);

    try {
      const result = await VehicleFinancingService.remove(id);

      if (result.code === OP_RESULT_CODES.OK) {
        // Remove from list
        setFinancingRecords((prev) => prev.filter((f) => f.id !== id));

        // Clear selection if removed
        setSelectedFinancing((prev) => (prev?.id === id ? null : prev));

        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to remove financing record"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to remove financing record"));
      return result;
    } finally {
      setIsRemoving(false);
    }
  }, []);

  // Remove multiple vehicle financing records
  const removeMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    setIsRemoving(true);
    setLastError(null);

    try {
      const result = await VehicleFinancingService.removeMany(ids);

      if (result.code === OP_RESULT_CODES.OK) {
        const idsSet = new Set(ids);

        // Remove from list
        setFinancingRecords((prev) => prev.filter((f) => !idsSet.has(f.id!)));

        // Clear selection if removed
        setSelectedFinancing((prev) =>
          prev?.id && idsSet.has(prev.id) ? null : prev
        );

        setLastError(null);
        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to remove financing records"));
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to remove financing records"));
      return result;
    } finally {
      setIsRemoving(false);
    }
  }, []);

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Refresh list with current filter/pagination
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list();
  }, [list]);

  // Select a financing record
  const select = useCallback((financing: VehicleFinancing | null) => {
    setSelectedFinancing(financing);
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedFinancing(null);
  }, []);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  const getActiveForCar = useCallback(
    (carId: string): VehicleFinancing | undefined => {
      return financingRecords.find(
        (f) => f.carId === carId && f.status === VEHICLE_FINANCING_STATUS.ACTIVE
      );
    },
    [financingRecords]
  );

  const isEmpty = useMemo(
    () => isLoaded && financingRecords.length === 0,
    [isLoaded, financingRecords]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Update filter when carId prop changes
  useEffect(() => {
    if (carId) {
      setFilter((prev) => ({
        ...prev,
        carId: [carId],
      }));
    }
  }, [carId]);

  // Handle user change - reset when user changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount
    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list();
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list();
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      financingRecords,
      selectedFinancing,
      isLoading,
      isLoaded,
      isEmpty,

      // Operations state
      isCreating,
      isUpdating,
      isRemoving,

      // Filter and pagination
      filter,
      pagination,
      sortBy,
      setFilter,
      setPagination,
      setSortBy,

      // Core operations
      list,
      get,
      getMany,
      create,
      update,
      remove,
      removeMany,

      // Convenience methods
      refresh,
      select,
      clearSelection,

      // Helper operations
      reset,

      // Derived data helpers
      getActiveForCar,

      // Error handling
      lastError,
      clearError,
    }),
    [
      financingRecords,
      selectedFinancing,
      isLoading,
      isLoaded,
      isEmpty,
      isCreating,
      isUpdating,
      isRemoving,
      filter,
      pagination,
      sortBy,
      list,
      get,
      getMany,
      create,
      update,
      remove,
      removeMany,
      refresh,
      select,
      clearSelection,
      reset,
      getActiveForCar,
      lastError,
      clearError,
    ]
  );
};